/**
 * Minimal OIDC authorization-code + PKCE client for the "Unterschriften-Mappe".
 *
 * signit is a static SPA, so this runs as a public client (no secret): the
 * browser logs in at the IdP (Keycloak), keeps the tokens in sessionStorage
 * (tab-scoped, gone when the tab closes) and calls ownCloud with them
 * directly. The signit backend never sees these tokens.
 *
 * Endpoints follow Keycloak's fixed layout under the realm issuer URL.
 */

export interface MappeConfig {
  ocisUrl: string;
  issuer: string;
  clientId: string;
  folder: string;
  outFolder: string;
}

/** Thrown when there is no (refreshable) session -- the caller must start a login. */
export class AuthRequired extends Error {
  constructor() {
    super("Anmeldung erforderlich");
  }
}

interface Tokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number; // ms since epoch
}

interface Pending {
  state: string;
  verifier: string;
}

const TOKENS_KEY = "signit.mappe.tokens";
const PENDING_KEY = "signit.mappe.pending";
// Refresh a little early so a token doesn't expire between check and use.
const EXPIRY_SKEW_MS = 30_000;
// oCIS needs the email claim (its users are matched by it), hence email/profile.
const SCOPE = "openid profile email";

const authEndpoint = (cfg: MappeConfig) => `${cfg.issuer}/protocol/openid-connect/auth`;
const tokenEndpoint = (cfg: MappeConfig) => `${cfg.issuer}/protocol/openid-connect/token`;
const redirectUri = () => `${window.location.origin}/`;

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength = 32): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

function readTokens(): Tokens | null {
  try {
    const raw = sessionStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function writeTokens(tokens: Tokens): void {
  sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
}

export function clearSession(): void {
  sessionStorage.removeItem(TOKENS_KEY);
  sessionStorage.removeItem(PENDING_KEY);
}

export function hasSession(): boolean {
  return readTokens() !== null;
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

async function requestToken(cfg: MappeConfig, params: Record<string, string>): Promise<Tokens> {
  const resp = await fetch(tokenEndpoint(cfg), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: cfg.clientId, ...params }),
  });
  if (!resp.ok) throw new Error(`Token-Anfrage fehlgeschlagen (HTTP ${resp.status})`);
  const body = (await resp.json()) as TokenResponse;
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: Date.now() + (body.expires_in ?? 60) * 1000,
  };
}

/** Redirects the browser to the IdP login; never resolves in practice. */
export async function startLogin(cfg: MappeConfig): Promise<void> {
  const pending: Pending = { state: randomString(16), verifier: randomString(48) };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  const url = new URL(authEndpoint(cfg));
  url.search = new URLSearchParams({
    response_type: "code",
    client_id: cfg.clientId,
    redirect_uri: redirectUri(),
    scope: SCOPE,
    state: pending.state,
    code_challenge: await challengeFor(pending.verifier),
    code_challenge_method: "S256",
  }).toString();
  window.location.assign(url.toString());
}

function stripQuery(): void {
  window.history.replaceState(null, "", window.location.pathname);
}

/**
 * Call once on page load. If the URL carries an authorization response,
 * finishes the login (token exchange) and returns true; returns false when
 * this is a normal page load. Throws with a user-readable message on failure.
 */
export async function handleRedirect(cfg: MappeConfig): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  const code = params.get("code");
  const state = params.get("state");
  if (!error && !(code && state)) return false;

  let pending: Pending | null = null;
  try {
    pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? "null") as Pending | null;
  } catch {
    pending = null;
  }
  sessionStorage.removeItem(PENDING_KEY);
  stripQuery();

  if (error) throw new Error(`Anmeldung fehlgeschlagen: ${params.get("error_description") ?? error}`);
  if (!pending || pending.state !== state) throw new Error("Anmeldung fehlgeschlagen: ungültiger Status.");

  writeTokens(
    await requestToken(cfg, {
      grant_type: "authorization_code",
      code: code as string,
      redirect_uri: redirectUri(),
      code_verifier: pending.verifier,
    }),
  );
  return true;
}

/** Returns a valid access token, refreshing it if needed; throws AuthRequired if it can't. */
export async function getAccessToken(cfg: MappeConfig): Promise<string> {
  const tokens = readTokens();
  if (!tokens) throw new AuthRequired();
  if (tokens.expiresAt - EXPIRY_SKEW_MS > Date.now()) return tokens.accessToken;
  if (!tokens.refreshToken) {
    clearSession();
    throw new AuthRequired();
  }
  try {
    const refreshed = await requestToken(cfg, {
      grant_type: "refresh_token",
      refresh_token: tokens.refreshToken,
    });
    // Keycloak may rotate the refresh token; keep the old one otherwise.
    refreshed.refreshToken ??= tokens.refreshToken;
    writeTokens(refreshed);
    return refreshed.accessToken;
  } catch {
    clearSession();
    throw new AuthRequired();
  }
}
